import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Info, X } from 'lucide-react';
import { registerToastApi } from '../../utils/notify';
import './toast.css';

/**
 * 应用内统一提示层。
 *
 * 背景：此前错误提示有三条并行通道 —— 19 处浏览器 `alert()`（阻塞、无法无障碍访问、
 * 样式与应用不一致）、`GlobalConfirmModal`、以及 `BootContext` 的内联降级条。
 * 同时大量 `catch {}` 把失败静默吞掉，界面上"加载失败"和"没有数据"长得一模一样。
 *
 * 这里提供单一的 toast 通道：非阻塞、可堆叠、可自动消失，带 `role="status"`/`alert`
 * 供屏幕阅读器播报。异步失败请配合 `utils/notify.ts` 的 `reportError` 使用。
 */

export type ToastKind = 'success' | 'error' | 'warn' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastInput {
  kind: ToastKind;
  message: string;
  /** 毫秒；0 表示不自动消失（错误默认停留更久） */
  duration?: number;
  action?: ToastAction;
}

interface ToastItem extends ToastInput {
  id: number;
}

interface ToastContextValue {
  push: (toast: ToastInput) => number;
  success: (message: string, action?: ToastAction) => number;
  error: (message: string, action?: ToastAction) => number;
  warn: (message: string, action?: ToastAction) => number;
  info: (message: string, action?: ToastAction) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/** 同时显示的条数上限（超出丢弃最旧的，避免刷屏） */
const MAX_TOASTS = 4;
const DEFAULT_DURATION: Record<ToastKind, number> = {
  success: 2600,
  info: 3200,
  warn: 5000,
  // 错误默认停留更久：用户需要时间读完并决定是否重试
  error: 7000,
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextIdRef = useRef(1);
  const timersRef = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const push = useCallback((input: ToastInput): number => {
    const id = nextIdRef.current++;
    setToasts(prev => {
      const next = [...prev, { ...input, id }];
      // 超出上限时丢弃最旧的（并清掉它的定时器）
      while (next.length > MAX_TOASTS) {
        const dropped = next.shift();
        if (dropped) {
          const timer = timersRef.current.get(dropped.id);
          if (timer) {
            clearTimeout(timer);
            timersRef.current.delete(dropped.id);
          }
        }
      }
      return next;
    });
    const duration = input.duration ?? DEFAULT_DURATION[input.kind];
    if (duration > 0) {
      timersRef.current.set(id, setTimeout(() => dismiss(id), duration));
    }
    return id;
  }, [dismiss]);

  // 卸载时清掉所有定时器，避免卸载后 setState
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(() => ({
    push,
    dismiss,
    success: (message, action) => push({ kind: 'success', message, action }),
    error: (message, action) => push({ kind: 'error', message, action }),
    warn: (message, action) => push({ kind: 'warn', message, action }),
    info: (message, action) => push({ kind: 'info', message, action }),
  }), [push, dismiss]);

  // 注册到模块级总线，让非组件代码（请求层/导出流程）也能发提示
  useEffect(() => registerToastApi({
    success: value.success,
    error: value.error,
    warn: value.warn,
    info: value.info,
  }), [value]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

const ICONS: Record<ToastKind, React.ReactNode> = {
  success: <CheckCircle2 size={15} />,
  error: <XCircle size={15} />,
  warn: <AlertTriangle size={15} />,
  info: <Info size={15} />,
};

function ToastViewport({ toasts, onDismiss }: { toasts: ToastItem[]; onDismiss: (id: number) => void }) {
  if (toasts.length === 0) return null;
  return (
    <div className="toast-viewport">
      {toasts.map(t => (
        <div
          key={t.id}
          className={`toast toast-${t.kind}`}
          // error 用 alert 立即播报，其余用 status 以免打断
          role={t.kind === 'error' ? 'alert' : 'status'}
          aria-live={t.kind === 'error' ? 'assertive' : 'polite'}
        >
          <span className="toast-icon">{ICONS[t.kind]}</span>
          <span className="toast-message">{t.message}</span>
          {t.action && (
            <button
              className="toast-action"
              onClick={() => { t.action!.onClick(); onDismiss(t.id); }}
            >
              {t.action.label}
            </button>
          )}
          <button className="toast-close" onClick={() => onDismiss(t.id)} title="关闭" aria-label="关闭提示">
            <X size={13} />
          </button>
        </div>
      ))}
    </div>
  );
}

/** 读取 toast API；必须在 ToastProvider 内使用 */
export function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error('useToast 必须在 <ToastProvider> 内使用');
  return ctx;
}
